import { Flexbox } from '@/ui/flexbox/flexbox';
import { Inline, Stack } from '@/ui/line/line';
import { styled } from 'goober';
import { ProjectTags } from '../tags/project-tags';
import { ProjectFilesTags } from './project-files-tags';
import { ProjectExternalLinksList } from '../external-links/project-external-links-list';
import { ProjectHeaderLeftBlock } from './project-header-left-block';
import type { ProjectHeaderForm } from './edit/use-project-header-form';
import { useProject } from '../use-project.query';
import { forwardRef, useEffect, useRef } from 'react';
import { useIsVisible } from '@/hooks/use-is-visible';
import {
  isProjectHeaderVisibleState,
  projectIdState,
} from '../../project.state';
import { ProjectTemplatesList } from './project-templates-list';
import { useSetAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Button } from '@/ui/button/button';
import {
  useAutoEnhanceConfigQuery,
  useDisableAutoEnhance,
} from '../../auto-enhance/use-auto-enhance-config';
import { Switch } from '@/ui/switch/switch';
import { notify } from '@/ui/snackbar/notify';
import { modalState } from '@/state/modal.state';
import { useEnhanceButton } from '../../enhance/use-enhance-button';
import { PendingCompaniesCounter } from './pending-companies-counter';
export interface ProjectHeaderProps {
  isLoading?: boolean;
  isCreateNewMode?: boolean;
  onEditSubmit: (values: ProjectHeaderForm) => Promise<void>;
}

export const ProjectHeader = ({
  isLoading,
  isCreateNewMode = false,
  onEditSubmit,
}: ProjectHeaderProps) => {
  const { t } = useTranslation('projects', { keyPrefix: 'projectOverview' });

  const projectId = useAtomValue(projectIdState);
  const { data: project, isLoading: isDataFetching } = useProject(projectId);
  const showSkeleton = isCreateNewMode ? Boolean(isLoading) : isDataFetching;
  const shouldRenderRightBlock = !isCreateNewMode && project;
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible<HTMLDivElement>(ref);
  const setIsProjectHeaderVisible = useSetAtom(isProjectHeaderVisibleState);

  const { hasPermissionToEnhanceProject, onEnhanceClick } = useEnhanceButton();
  const { data: autoEnhanceConfig, isLoading: isAutoConfigLoading } =
    useAutoEnhanceConfigQuery(project?.id ?? undefined);
  const { mutate: disableAuto, isPending: isDisablingAuto } =
    useDisableAutoEnhance(project?.id ?? undefined);

  const setModalState = useSetAtom(modalState);

  const handleAutoEnhanceToggle = (nextState: boolean) => {
    if (nextState) {
      return setModalState({ state: 'autoEnhanceIntro' });
    }

    if (project?.id) {
      disableAuto(undefined, {
        onSuccess: () => {
          notify({
            message: t`autoEnhanceDisabled`,
          });
        },
      });
    }
  };

  useEffect(() => {
    setIsProjectHeaderVisible(isVisible);
  }, [isVisible, setIsProjectHeaderVisible]);

  return (
    <Container data-testid="project-overview-header" ref={ref}>
      <BlocksWrapper templateColumns="55fr 45fr">
        <ProjectHeaderLeftBlock
          isLoading={showSkeleton}
          onEditSubmit={onEditSubmit}
          isCreateNewMode={isCreateNewMode}
        />

        {shouldRenderRightBlock && (
          <Flexbox direction="column" justify="space-between" height="100%">
            <Flexbox fullHeight alignItems="flex-start">
              <Stack gap="18px">
                <ProjectTags
                  projectId={project?.id}
                  tags={project?.tags ?? []}
                  isLoading={isDataFetching}
                />
                <ProjectFilesTags />
                <ProjectExternalLinksList />
              </Stack>
            </Flexbox>
            <ProjectTemplatesList />
            <Flexbox fullWidth gap="16px" padding="16px 0 0">
              {hasPermissionToEnhanceProject && (
                <Inline alignItems="center" gap="8px">
                  <Switch
                    dataTestId="auto-enhance-toggle"
                    checked={autoEnhanceConfig?.enabled ?? false}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleAutoEnhanceToggle(event.target.checked)
                    }
                    disabled={
                      isAutoConfigLoading || isDisablingAuto || isLoading
                    }
                  />
                  <Label>{t`autoEnhanceToggle`}</Label>
                </Inline>
              )}
              {hasPermissionToEnhanceProject && (
                <Button
                  data-testid="project-listing-enhance-button"
                  onClick={onEnhanceClick}
                  startIcon="Sparkle"
                  variant="primary"
                >
                  {t`enhance`}
                </Button>
              )}
              <PendingCompaniesCounter />
            </Flexbox>
          </Flexbox>
        )}
      </BlocksWrapper>
    </Container>
  );
};

const Container = styled('div', forwardRef)`
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.project.header};
  padding: 42px 48px;
  background-color: ${({ theme }) => theme.colors.basics.white};
`;

const BlocksWrapper = styled(Inline)`
  gap: 30px;

  @media (min-width: 1620px) {
    gap: 40px;
  }
`;

const Label = styled('span')`
  ${({ theme }) => theme.typography.widget.smallText}
  color: ${({ theme }) => theme.colors.gray.c12};
  width: 165px;
`;
